/*
 * fbp-smart-personalisation.js — P1.08
 *
 * Smart helpers under the Globo personalisation textarea on product pages:
 *   [Custom art upload] [Zipper on long side] [Custom size]
 * Clicking a chip pre-writes the request text into the textarea (appending,
 * never overwriting what the customer already typed). The art chip turns the
 * textarea into a drop zone and uploads up to 2 files (<=1GB each) straight to
 * the FBP upload broker (Google Drive resumable), then writes the file links
 * into the textarea so the customer SEES it worked.
 *
 * The art chip only renders when the broker health endpoint answers, so
 * customers never meet a dead upload button. Zipper/size chips always work.
 * No dependency on Globo internals beyond .gpo-container + its textarea.
 */
(function () {
  'use strict';
  if (!/\/products\//.test(location.pathname)) return;

  // Droplet-direct (bypasses Shopify's app proxy, which blocks POST subpaths and
  // caps upload size). Cross-origin; the broker returns CORS headers.
  var BROKER_BASE = 'https://chat.furrybodypillows.com/customer-profiles-v1/uploads';
  // 2026-09-02 fix (customer report "upload http 404"): the broker answers
  // /session with a path relative to ITS OWN service root ("/uploads/put/<token>").
  // Passed straight to XHR from a product page it resolved against
  // furrybodypillows.com, where Shopify returns 404, so every art upload failed.
  // Always resolve broker-returned URLs against the broker, never the page.
  var BROKER_ROOT = BROKER_BASE.slice(0, BROKER_BASE.lastIndexOf('/uploads'));
  var BROKER_PATH_START = BROKER_ROOT.indexOf('/', BROKER_ROOT.indexOf('//') + 2);
  var BROKER_ORIGIN = BROKER_PATH_START === -1 ? BROKER_ROOT : BROKER_ROOT.slice(0, BROKER_PATH_START);
  var BROKER_PREFIX = BROKER_ROOT.slice(BROKER_ORIGIN.length);
  function brokerUrl(u) {
    if (!u || typeof u !== 'string') throw new Error('broker returned no upload URL');
    var lower = u.toLowerCase();
    if (lower.indexOf('https://') === 0 || lower.indexOf('http://') === 0) return u;
    if (BROKER_PREFIX && u.indexOf(BROKER_PREFIX + '/') === 0) return BROKER_ORIGIN + u;
    if (u.charAt(0) === '/') return BROKER_ROOT + u;
    return BROKER_BASE + '/' + u;
  }
  var MAX_FILES = 2;
  var MAX_BYTES = 1024 * 1024 * 1024; // 1 GB per file
  var TEXTS = {
    artIntro: 'I would like my daki to be made with custom artwork:',
    zipper: 'I would like my daki to have the Zipper along the long side of the daki instead of at the bottom.',
    size: 'I would like my daki to be: (enter size here: example, 170x70cm)'
  };

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  function findTextarea() {
    var container = document.querySelector('.gpo-container');
    if (!container) return null;
    // Globo renders either a textarea or a single-line text input depending on
    // the option type — support both.
    return container.querySelector('textarea') ||
      container.querySelector('input.gpo-text, input.gpo-input[type="text"], input[type="text"]');
  }

  function sepFor(el) { return el.tagName === 'TEXTAREA' ? '\n' : ' | '; }

  function dispatchInput(el) {
    ['input', 'change'].forEach(function (type) {
      var ev; try { ev = new Event(type, { bubbles: true }); } catch (e) { ev = document.createEvent('Event'); ev.initEvent(type, true, false); }
      el.dispatchEvent(ev);
    });
  }

  function appendText(ta, text, selectToken) {
    var cur = ta.value.replace(/\s+$/, '');
    if (cur.indexOf(text) !== -1) return; // already added
    ta.value = (cur ? cur + sepFor(ta) : '') + text;
    dispatchInput(ta);
    ta.focus();
    if (selectToken) {
      var idx = ta.value.lastIndexOf(selectToken);
      if (idx !== -1) { try { ta.setSelectionRange(idx, idx + selectToken.length); } catch (e) {} }
    } else {
      try { ta.setSelectionRange(ta.value.length, ta.value.length); } catch (e) {}
    }
  }

  function replaceOrAppendArtBlock(ta, links) {
    // Build "I would like ... : artwork 1: <link> artwork 2: <link>" using the
    // field's separator (newline in a textarea, " | " in a single-line input).
    var sep = sepFor(ta);
    var escSep = sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var re = new RegExp(TEXTS.artIntro.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(' + escSep + 'artwork \\d+: \\S*)*(' + escSep + ')?');
    var cur = ta.value;
    if (!links.length) {
      // all files removed -> take the whole art block back out of the box
      ta.value = cur.replace(re, '').replace(/^\s*\|\s*/, '').replace(/\s*\|\s*$/, '').replace(/\s+$/, '');
      dispatchInput(ta);
      return;
    }
    var block = TEXTS.artIntro;
    for (var i = 0; i < links.length; i += 1) block += sep + 'artwork ' + (i + 1) + ': ' + links[i];
    if (re.test(cur) && cur.match(re)[0]) ta.value = cur.replace(re, block);
    else ta.value = (cur.replace(/\s+$/, '') ? cur.replace(/\s+$/, '') + sep : '') + block;
    dispatchInput(ta);
  }

  /* ---------- styles ---------- */
  var style = document.createElement('style');
  style.textContent = [
    '.fbp-sp-chips{display:flex;flex-wrap:wrap;gap:8px;margin:10px 0 4px}',
    '.fbp-sp-chip{display:inline-flex;align-items:center;gap:6px;border:1.5px solid #c9963e;background:#fff;color:#8a6420;',
    'border-radius:999px;padding:7px 14px;font-size:12.5px;font-weight:600;cursor:pointer;line-height:1.2;',
    'transition:background .15s,color .15s,box-shadow .15s;user-select:none}',
    '.fbp-sp-chip:hover{background:linear-gradient(135deg,#c9963e,#e8b85c);color:#fff;box-shadow:0 3px 8px rgba(201,150,62,.35)}',
    '.fbp-sp-chip[disabled]{opacity:.55;cursor:default}',
    '.fbp-sp-note{font-size:11.5px;color:#8a8a83;margin:2px 0 0}',
    '.fbp-sp-drop{outline:2px dashed #c9963e !important;outline-offset:3px;background:#fdf8ef !important}',
    '.fbp-sp-progress{font-size:12px;color:#8a6420;margin-top:6px;display:none}',
    '.fbp-sp-progress b{font-weight:700}',
    '.fbp-sp-files{display:flex;flex-direction:column;gap:6px;margin-top:8px}',
    '.fbp-sp-file{display:flex;align-items:center;gap:8px;background:#fdf8ef;border:1px solid #e8b85c;',
    'border-radius:8px;padding:7px 10px;font-size:12.5px;color:#6b4f18;line-height:1.3}',
    '.fbp-sp-file .ico{flex:none}',
    '.fbp-sp-file .nm{font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:46%}',
    '.fbp-sp-file a{color:#185fa5;font-weight:600;text-decoration:underline;flex:none}',
    '.fbp-sp-file .rm{margin-left:auto;flex:none;border:0;background:none;color:#a08040;cursor:pointer;',
    'font-size:14px;line-height:1;padding:2px 4px;border-radius:4px}',
    '.fbp-sp-file .rm:hover{background:#f3e3c2;color:#7a5a10}'
  ].join('');
  document.head.appendChild(style);

  /* ---------- upload lane ---------- */
  // links: [{ name, link }] — rendered as clickable cards under the box AND
  // written into the personalisation text (which is what rides on the order).
  var uploadState = { links: [], busy: false, filesEl: null, ta: null, progressEl: null };

  function renderFiles() {
    var el = uploadState.filesEl;
    if (!el) return;
    el.innerHTML = '';
    uploadState.links.forEach(function (f, idx) {
      var row = document.createElement('div');
      row.className = 'fbp-sp-file';
      var ico = document.createElement('span'); ico.className = 'ico'; ico.textContent = '🖼️';
      var nm = document.createElement('span'); nm.className = 'nm'; nm.textContent = 'artwork ' + (idx + 1) + ': ' + f.name; nm.title = f.name;
      var a = document.createElement('a'); a.href = f.link; a.target = '_blank'; a.rel = 'noopener'; a.textContent = 'view file';
      var rm = document.createElement('button'); rm.type = 'button'; rm.className = 'rm'; rm.textContent = '✕'; rm.title = 'Remove this file from your request';
      rm.addEventListener('click', function () {
        uploadState.links.splice(idx, 1);
        replaceOrAppendArtBlock(uploadState.ta, uploadState.links.map(function (x) { return x.link; }));
        renderFiles();
        if (uploadState.progressEl) setProgress(uploadState.progressEl, uploadState.links.length ? '' : 'File removed. You can upload again any time.');
      });
      row.appendChild(ico); row.appendChild(nm); row.appendChild(a); row.appendChild(rm);
      el.appendChild(row);
    });
  }

  function humanSize(n) {
    if (n >= 1e9) return (n / 1e9).toFixed(2) + ' GB';
    if (n >= 1e6) return (n / 1e6).toFixed(1) + ' MB';
    return Math.max(1, Math.round(n / 1e3)) + ' KB';
  }

  function setProgress(el, msg) { el.style.display = msg ? 'block' : 'none'; el.innerHTML = msg || ''; }

  function brokerJson(path, opts) {
    return fetch(BROKER_BASE + path, Object.assign({ headers: { 'content-type': 'application/json' } }, opts))
      .then(function (r) { if (!r.ok) throw new Error('broker ' + r.status); return r.json(); });
  }

  function uploadOne(file, progressEl, slot) {
    return brokerJson('/session', {
      method: 'POST',
      body: JSON.stringify({ filename: file.name, size: file.size, mime: file.type || 'application/octet-stream', page: location.pathname })
    }).then(function (session) {
      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('PUT', brokerUrl(session.uploadUrl), true);
        xhr.upload.onprogress = function (e) {
          if (e.lengthComputable) {
            var pct = Math.round((e.loaded / e.total) * 100);
            setProgress(progressEl, 'Uploading <b>' + file.name + '</b> (' + humanSize(file.size) + ') — <b>' + pct + '%</b>');
          }
        };
        xhr.onerror = function () { reject(new Error('upload network error')); };
        xhr.onload = function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            var fileId = null;
            try { fileId = JSON.parse(xhr.responseText).id; } catch (e) {}
            resolve({ token: session.token, fileId: fileId });
          } else reject(new Error('upload http ' + xhr.status));
        };
        xhr.send(file);
      });
    }).then(function (done) {
      return brokerJson('/complete', { method: 'POST', body: JSON.stringify({ token: done.token, fileId: done.fileId }) });
    }).then(function (fin) { return fin.link; });
  }

  function handleFiles(fileList, ta, progressEl) {
    if (uploadState.busy) return;
    var files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;
    var room = MAX_FILES - uploadState.links.length;
    if (room <= 0) { setProgress(progressEl, 'You already added ' + MAX_FILES + ' files. Remove one below (✕) to swap it.'); return; }
    files = files.slice(0, room);
    for (var i = 0; i < files.length; i += 1) {
      if (files[i].size > MAX_BYTES) { setProgress(progressEl, '<b>' + files[i].name + '</b> is larger than 1 GB. Please compress it or split it.'); return; }
    }
    uploadState.busy = true;
    var chain = Promise.resolve();
    files.forEach(function (file) {
      chain = chain.then(function () {
        return uploadOne(file, progressEl, uploadState.links.length + 1).then(function (link) {
          uploadState.links.push({ name: file.name, link: link });
          replaceOrAppendArtBlock(ta, uploadState.links.map(function (x) { return x.link; }));
          renderFiles();
        });
      });
    });
    chain.then(function () {
      uploadState.busy = false;
      setProgress(progressEl, 'Upload complete — your artwork is attached below and added to your request. We can see the file' + (uploadState.links.length > 1 ? 's' : '') + '.');
    }).catch(function (err) {
      uploadState.busy = false;
      setProgress(progressEl, 'Upload failed (' + err.message + '). Please try again, or email the file to info@furrybodypillows.com.');
    });
  }

  function wireDropZone(ta, progressEl) {
    ['dragenter', 'dragover'].forEach(function (t) {
      ta.addEventListener(t, function (e) { e.preventDefault(); ta.classList.add('fbp-sp-drop'); });
    });
    ['dragleave', 'drop'].forEach(function (t) {
      ta.addEventListener(t, function (e) { if (t === 'drop') e.preventDefault(); ta.classList.remove('fbp-sp-drop'); });
    });
    ta.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) handleFiles(e.dataTransfer.files, ta, progressEl);
    });
  }

  /* ---------- mount ---------- */
  function mount(ta) {
    if (ta.dataset.fbpSpMounted) return;
    ta.dataset.fbpSpMounted = '1';

    var chips = document.createElement('div');
    chips.className = 'fbp-sp-chips';

    var progressEl = document.createElement('div');
    progressEl.className = 'fbp-sp-progress';

    function chip(label, title) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fbp-sp-chip';
      b.textContent = label;
      if (title) b.title = title;
      chips.appendChild(b);
      return b;
    }

    var zipBtn = chip('✨ Zipper on long side', 'Ask for the zipper along the long side');
    zipBtn.addEventListener('click', function () { appendText(ta, TEXTS.zipper); });

    var sizeBtn = chip('📏 Custom size', 'Ask for a custom pillow size');
    sizeBtn.addEventListener('click', function () { appendText(ta, TEXTS.size, '(enter size here: example, 170x70cm)'); });

    var note = document.createElement('p');
    note.className = 'fbp-sp-note';
    note.textContent = 'Tap a button and we pre-write the request for you — edit it however you like.';

    // Mount BELOW the whole input wrap (not inside it) so the field keeps its
    // full width and the chips form their own row underneath.
    var anchor = ta.closest('.gpo-input-wrap') || ta;
    anchor.parentNode.insertBefore(chips, anchor.nextSibling);
    chips.parentNode.insertBefore(note, chips.nextSibling);
    chips.parentNode.insertBefore(progressEl, note.nextSibling);

    // Uploaded-artwork cards live right under the progress line — the customer
    // sees their files without scrolling inside the text box.
    var filesEl = document.createElement('div');
    filesEl.className = 'fbp-sp-files';
    chips.parentNode.insertBefore(filesEl, progressEl.nextSibling);
    uploadState.filesEl = filesEl;
    uploadState.ta = ta;
    uploadState.progressEl = progressEl;

    // Art upload chip appears only when the broker is alive.
    fetch(BROKER_BASE + '/health').then(function (r) { return r.ok ? r.json() : null; }).then(function (h) {
      if (!h || !h.ok) return;
      var artBtn = chip('🎨 Custom art upload', 'Upload up to 2 files, 1 GB each');
      chips.insertBefore(artBtn, chips.firstChild);
      var picker = document.createElement('input');
      picker.type = 'file';
      picker.multiple = true;
      picker.accept = 'image/*,.psd,.tif,.tiff,.zip,.7z,.rar,.pdf,.clip,.sai,.kra,.xcf';
      picker.style.display = 'none';
      chips.appendChild(picker);
      artBtn.addEventListener('click', function () {
        appendText(ta, TEXTS.artIntro);
        setProgress(progressEl, 'Choose up to 2 files (max 1 GB each) — or drag them onto the text box.');
        picker.click();
      });
      picker.addEventListener('change', function () { handleFiles(picker.files, ta, progressEl); picker.value = ''; });
      wireDropZone(ta, progressEl);
      note.textContent = 'Tap a button and we pre-write the request — or drop your art files right onto the text box.';
    }).catch(function () { /* broker offline: chips zipper/size still work */ });
  }

  function boot() {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      var ta = findTextarea();
      if (ta) { clearInterval(timer); mount(ta); }
      else if (tries > 40) clearInterval(timer); // give up after ~20s (no Globo box on this product)
    }, 500);
  }

  onReady(boot);
})();
